/**
 * Aba "Contratos" do painel do paciente.
 *
 * Mostra os envios de assinatura daquele paciente e permite disparar um novo
 * sem sair do prontuário — é o momento em que a clínica costuma precisar, logo
 * depois de fechar um orçamento ou um plano de tratamento.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/AddRounded';
import DrawIcon from '@mui/icons-material/GestureRounded';
import PdfIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';

import ReceiptIcon from '@mui/icons-material/ReceiptLongRounded';

import NewSignatureWizard from './NewSignatureWizard';
import CollectSignaturesDialog from './CollectSignaturesDialog';
import MedicalRecordDeliveryDialog from './MedicalRecordDeliveryDialog';
import {
  cancelMedicalRecordDelivery,
  getEnvelope,
  listEnvelopes,
  listMedicalRecordDeliveries,
  openEnvelopePdf,
} from '../../services/signatureService';
import {
  ENVELOPE_STATUS_LABELS,
  ENVELOPE_STATUS_TONES,
  MEDICAL_RECORD_FORMAT_LABELS,
  MEDICAL_RECORD_RECIPIENT_LABELS,
  SIGNATURE_PURPOSE_LABELS,
} from '../../constants/signature';
import type { EnvelopeDetail, MedicalRecordDelivery, SignatureEnvelope } from '../../types/signature';

function deliverySummary(delivery: MedicalRecordDelivery): string {
  const parts = [
    delivery.recipient_type === 'patient'
      ? 'Recebido pelo paciente'
      : `Recebido por ${delivery.recipient_name} (${MEDICAL_RECORD_RECIPIENT_LABELS[delivery.recipient_type].toLowerCase()})`,
    MEDICAL_RECORD_FORMAT_LABELS[delivery.delivery_format],
  ];
  if (delivery.page_count) parts.push(`${delivery.page_count} pág.`);
  if (delivery.delivered_by_name) parts.push(`entregue por ${delivery.delivered_by_name}`);
  return parts.join('  ·  ');
}

interface PatientContractsTabProps {
  patientId: string;
  patientName: string;
  patientCpf?: string | null;
  patientPhone?: string | null;
  /** Dentro da aba Docs do painel — sem padding duplicado nem título de página. */
  embedded?: boolean;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

export default function PatientContractsTab({
  patientId,
  patientName,
  patientCpf,
  patientPhone,
  embedded = false,
}: PatientContractsTabProps) {
  const shellSx = embedded
    ? { p: { xs: 1.5, sm: 2 } }
    : { p: { xs: 1.5, sm: 2, md: 3 } };
  const [envelopes, setEnvelopes] = useState<SignatureEnvelope[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const [collectDetail, setCollectDetail] = useState<EnvelopeDetail | null>(null);
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  /** Por envelope_id. Vazio quando o banco ainda não tem a tabela de entregas. */
  const [deliveries, setDeliveries] = useState<Record<string, MedicalRecordDelivery>>({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, deliveryList] = await Promise.all([
        listEnvelopes({ patient_id: patientId }),
        listMedicalRecordDeliveries(patientId).catch(() => [] as MedicalRecordDelivery[]),
      ]);
      setEnvelopes(list);
      setDeliveries(
        Object.fromEntries(
          deliveryList.filter((d) => d.envelope_id).map((d) => [d.envelope_id as string, d]),
        ),
      );
      setError(null);
    } catch {
      setError('Não foi possível carregar os contratos deste paciente.');
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    load();
  }, [load]);

  const openCollect = async (envelopeId: string) => {
    try {
      setCollectDetail(await getEnvelope(envelopeId));
    } catch (e: any) {
      setError(e?.message || 'Não foi possível abrir o envio.');
    }
  };

  const handleCancelDelivery = async (delivery: MedicalRecordDelivery) => {
    if (!window.confirm('Cancelar o registro desta entrega de prontuário?')) return;
    try {
      await cancelMedicalRecordDelivery(delivery.id);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Não foi possível cancelar a entrega.');
    }
  };

  if (creating) {
    return (
      <Box sx={shellSx}>
        <NewSignatureWizard
          initialPatient={{
            id: patientId,
            full_name: patientName,
            cpf: patientCpf,
            phone: patientPhone,
          }}
          onCancel={() => setCreating(false)}
          onCreated={(detail, sent) => {
            setCreating(false);
            load();
            if (sent && detail.envelope.delivery_method === 'device') setCollectDetail(detail);
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={shellSx}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
        sx={{ mb: embedded ? 1.5 : 2 }}
      >
        {!embedded && (
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Contratos e termos</Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ ml: embedded ? 0 : 'auto' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReceiptIcon />}
            onClick={() => setDeliveryOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, color: '#0f766e', borderColor: '#99f6e4' }}
          >
            Entrega de prontuário
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              bgcolor: '#0D9488',
              '&:hover': { bgcolor: '#0f766e' },
            }}
          >
            Enviar para assinatura
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && (
        <Box sx={{ py: 6, display: 'grid', placeItems: 'center' }}>
          <CircularProgress size={24} />
        </Box>
      )}

      {!loading && envelopes.length === 0 && (
        <Stack spacing={1.25} alignItems="center" sx={{ py: 6, textAlign: 'center' }}>
          <DescriptionIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
          <Typography sx={{ fontSize: 13.5, color: 'text.secondary', maxWidth: 340 }}>
            Nenhum contrato enviado para este paciente ainda.
          </Typography>
        </Stack>
      )}

      <Stack spacing={1.25}>
        {envelopes.map((envelope) => {
          const signers = envelope.signers || [];
          const signed = signers.filter((s) => s.status === 'signed').length;
          const pending = signers.length - signed;
          const purpose = envelope.metadata?.purpose;
          const delivery = deliveries[envelope.id];

          return (
            <Paper key={envelope.id} variant="outlined" sx={{ borderRadius: 2, p: 1.75 }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.25}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{envelope.title}</Typography>
                    {purpose && SIGNATURE_PURPOSE_LABELS[purpose] && (
                      <Chip
                        size="small"
                        label={SIGNATURE_PURPOSE_LABELS[purpose]}
                        sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: '#F0FDFA', color: '#0f766e' }}
                      />
                    )}
                  </Stack>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>
                    {signed}/{signers.length} assinaram
                    {envelope.sent_at && `  ·  enviado em ${formatDateTime(envelope.sent_at)}`}
                  </Typography>
                  {delivery && (
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{deliverySummary(delivery)}</Typography>
                  )}
                </Box>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={ENVELOPE_STATUS_LABELS[envelope.status]}
                    color={
                      ENVELOPE_STATUS_TONES[envelope.status] === 'default'
                        ? undefined
                        : ENVELOPE_STATUS_TONES[envelope.status]
                    }
                    variant="outlined"
                    sx={{ fontSize: 11, fontWeight: 600 }}
                  />

                  {envelope.status === 'completed' ? (
                    <Button
                      size="small"
                      startIcon={<PdfIcon fontSize="small" />}
                      onClick={() => openEnvelopePdf(envelope.id)}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Ver PDF
                    </Button>
                  ) : (
                    pending > 0 &&
                    envelope.status !== 'cancelled' &&
                    envelope.status !== 'draft' && (
                      <Button
                        size="small"
                        startIcon={<DrawIcon fontSize="small" />}
                        onClick={() => openCollect(envelope.id)}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Coletar
                      </Button>
                    )
                  )}
                  {delivery?.status === 'pending_signature' && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleCancelDelivery(delivery)}
                      sx={{ textTransform: 'none' }}
                    >
                      Cancelar
                    </Button>
                  )}
                </Stack>
              </Stack>
            </Paper>
          );
        })}
      </Stack>

      {collectDetail && (
        <CollectSignaturesDialog
          open
          onClose={() => {
            setCollectDetail(null);
            load();
          }}
          envelopeId={collectDetail.envelope.id}
          signers={collectDetail.signers}
        />
      )}

      <MedicalRecordDeliveryDialog
        open={deliveryOpen}
        onClose={() => {
          setDeliveryOpen(false);
          load();
        }}
        patient={{ id: patientId, full_name: patientName, cpf: patientCpf, phone: patientPhone }}
      />
    </Box>
  );
}
